import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Save, X, User, Mail, Phone, MapPin, 
  Calendar, CreditCard, FileText, Plus, Trash2, ArrowLeft,
  Check, ChevronRight, ChevronLeft, AlertCircle, Building
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import InputMask from '../../components/Form/InputMask';

const schema = yup.object().shape({
  tipoPessoa: yup.string().required('Tipo é obrigatório'),
  cpfCnpj: yup.string().required('CPF/CNPJ é obrigatório'),
  nome: yup.string().required('Nome é obrigatório'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  rgIe: yup.string().nullable(),
  dataNascimento: yup.date().nullable(),
  nomePai: yup.string().nullable(),
  nomeMae: yup.string().nullable(),
  diaVencimento: yup.number().min(1).max(31).required('Dia de vencimento é obrigatório'),
  senhaPortal: yup.string().nullable(),
  observacoes: yup.string().nullable()
});

const STEPS = [
  { id: 1, name: 'Dados Básicos', icon: User, description: 'Informações principais' },
  { id: 2, name: 'Endereços', icon: MapPin, description: 'Endereços do cliente' },
  { id: 3, name: 'Telefones', icon: Phone, description: 'Contatos telefônicos' },
  { id: 4, name: 'Resumo', icon: FileText, description: 'Revisar e finalizar' }
];

const ClienteForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cidades, setCidades] = useState([]);
  const [enderecos, setEnderecos] = useState([]);
  const [telefones, setTelefones] = useState([]);
  
  const [enderecosOriginais, setEnderecosOriginais] = useState([]);
  const [telefonesOriginais, setTelefonesOriginais] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(schema)
  });

  const tipoPessoa = watch('tipoPessoa', 'F');

  useEffect(() => {
    fetchCidades();
    if (isEdit) {
      fetchCliente();
    }
  }, [id]);

  const fetchCidades = async () => {
    try {
      const response = await api.get('/cidades?limit=100');
      setCidades(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
    }
  };

  const fetchCliente = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clientes/${id}`);
      const cliente = response.data;
      
      let dataNascimentoFormatada = null;
      if (cliente.dataNascimento) {
        const date = new Date(cliente.dataNascimento);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dataNascimentoFormatada = `${year}-${month}-${day}`;
      }
      
      const formData = {
        tipoPessoa: cliente.tipoPessoa || 'F',
        cpfCnpj: cliente.cpfCnpj || '',
        nome: cliente.nome || '',
        email: cliente.email || '',
        rgIe: cliente.rgIe || '',
        dataNascimento: dataNascimentoFormatada,
        nomePai: cliente.nomePai || '',
        nomeMae: cliente.nomeMae || '',
        diaVencimento: cliente.diaVencimento || 1,
        senhaPortal: '',
        observacoes: cliente.observacoes || ''
      };
      
      reset(formData);
      
      const enderecosData = cliente.enderecos || [];
      const telefonesData = cliente.telefones || [];
      
      enderecosData.forEach(endereco => {
        if (endereco.cep && endereco.cep.length === 8 && !endereco.cep.includes('-')) {
          endereco.cep = `${endereco.cep.substr(0, 5)}-${endereco.cep.substr(5)}`;
        }
      });
      
      telefonesData.forEach(telefone => {
        if (telefone.numero && !telefone.numero.includes('(')) {
          const num = telefone.numero.replace(/\D/g, '');
          if (num.length === 11) {
            telefone.numero = `(${num.substr(0, 2)}) ${num.substr(2, 5)}-${num.substr(7)}`;
          } else if (num.length === 10) {
            telefone.numero = `(${num.substr(0, 2)}) ${num.substr(2, 4)}-${num.substr(6)}`;
          }
        }
      });
      
      setEnderecos(enderecosData);
      setTelefones(telefonesData);
      
      setEnderecosOriginais(JSON.parse(JSON.stringify(enderecosData)));
      setTelefonesOriginais(JSON.parse(JSON.stringify(telefonesData)));
    } catch (error) {
      toast.error('Erro ao carregar cliente');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    let isValid = true;
    
    if (currentStep === 1) {
      isValid = await trigger(['tipoPessoa', 'cpfCnpj', 'nome', 'email', 'diaVencimento']);
    }
    
    if (isValid && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      for (let i = 0; i < enderecos.length; i++) {
        const endereco = enderecos[i];
        if (!endereco.cidadeId) {
          toast.error(`Endereço ${i + 1}: Cidade é obrigatória`);
          setLoading(false);
          return;
        }
        if (!endereco.logradouro || !endereco.numero) {
          toast.error(`Endereço ${i + 1}: Logradouro e número são obrigatórios`);
          setLoading(false);
          return;
        }
      }
      
      for (let i = 0; i < telefones.length; i++) {
        const telefone = telefones[i];
        const numeroLimpo = telefone.numero ? telefone.numero.replace(/\D/g, '') : '';
        if (!numeroLimpo || numeroLimpo.length < 10) {
          toast.error(`Telefone ${i + 1}: Número inválido ou incompleto (mínimo 10 dígitos)`);
          setLoading(false);
          return;
        }
      }
      
      const clienteData = {
        ...data,
        rgIe: data.rgIe || null,
        dataNascimento: data.dataNascimento || null,
        nomePai: data.nomePai || null,
        nomeMae: data.nomeMae || null,
        senhaPortal: data.senhaPortal || null,
        observacoes: data.observacoes || null,
        diaVencimento: parseInt(data.diaVencimento)
      };
      
      if (isEdit) {
        await api.patch(`/clientes/${id}`, clienteData);
        
        const enderecosRemovidos = enderecosOriginais.filter(
          original => !enderecos.find(e => e.id === original.id)
        );
        
        for (const endereco of enderecosRemovidos) {
          try {
            await api.delete(`/enderecos/${endereco.id}`);
          } catch (error) {
            console.error(`Erro ao deletar endereço ${endereco.id}:`, error);
          }
        }
        
        for (const endereco of enderecos) {
          try {
            const enderecoData = {
              cep: endereco.cep ? endereco.cep.replace(/\D/g, '') : '',
              logradouro: endereco.logradouro || '',
              numero: endereco.numero || '',
              complemento: endereco.complemento || '',
              bairro: endereco.bairro || '',
              cidadeId: endereco.cidadeId ? parseInt(endereco.cidadeId) : null,
              tipo: endereco.tipo || 'instalacao',
              referencia: endereco.referencia || '',
              clienteId: parseInt(id)
            };
            
            if (endereco.id) {
              await api.patch(`/enderecos/${endereco.id}`, enderecoData);
            } else {
              await api.post('/enderecos', enderecoData);
            }
          } catch (error) {
            console.error('Erro ao salvar endereço:', error);
            throw error;
          }
        }
        
        const telefonesRemovidos = telefonesOriginais.filter(
          original => !telefones.find(t => t.id === original.id)
        );
        
        for (const telefone of telefonesRemovidos) {
          try {
            await api.delete(`/telefones/${telefone.id}`);
          } catch (error) {
            console.error(`Erro ao deletar telefone ${telefone.id}:`, error);
          }
        }
        
        for (const telefone of telefones) {
          try {
            const telefoneData = {
              numero: telefone.numero ? telefone.numero.replace(/\D/g, '') : '',
              tipo: telefone.tipo || 'celular',
              whatsapp: telefone.whatsapp || false,
              principal: telefone.principal || false,
              clienteId: parseInt(id)
            };
            
            if (telefone.id) {
              await api.patch(`/telefones/${telefone.id}`, telefoneData);
            } else {
              await api.post('/telefones', telefoneData);
            }
          } catch (error) {
            console.error('Erro ao salvar telefone:', error);
            throw error;
          }
        }
        
        toast.success('Cliente atualizado com sucesso');
      } else {
        const response = await api.post('/clientes', clienteData);
        const clienteId = response.data.id;
        
        for (const endereco of enderecos) {
          try {
            const enderecoData = {
              cep: endereco.cep ? endereco.cep.replace(/\D/g, '') : '',
              logradouro: endereco.logradouro || '',
              numero: endereco.numero || '',
              complemento: endereco.complemento || '',
              bairro: endereco.bairro || '',
              cidadeId: endereco.cidadeId ? parseInt(endereco.cidadeId) : null,
              tipo: endereco.tipo || 'instalacao',
              referencia: endereco.referencia || '',
              clienteId: parseInt(clienteId)
            };
            
            await api.post('/enderecos', enderecoData);
          } catch (error) {
            console.error('Erro ao salvar endereço:', error);
            throw error;
          }
        }
        
        for (const telefone of telefones) {
          try {
            const telefoneData = {
              numero: telefone.numero ? telefone.numero.replace(/\D/g, '') : '',
              tipo: telefone.tipo || 'celular',
              whatsapp: telefone.whatsapp || false,
              principal: telefone.principal || false,
              clienteId: parseInt(clienteId)
            };
            
            await api.post('/telefones', telefoneData);
          } catch (error) {
            console.error('Erro ao salvar telefone:', error);
            throw error;
          }
        }
        
        toast.success('Cliente cadastrado com sucesso');
      }
      
      navigate('/clientes');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      toast.error(`Erro ao salvar cliente: ${errorMessage}`);
      console.error('Erro completo:', error);
    } finally {
      setLoading(false);
    }
  };

  const addEndereco = () => {
    setEnderecos([...enderecos, {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidadeId: '',
      tipo: 'instalacao',
      referencia: ''
    }]);
  };

  const removeEndereco = async (index) => {
    const endereco = enderecos[index];
    
    if (endereco.id && isEdit) {
      const confirmDelete = window.confirm(
        'Tem certeza que deseja remover este endereço? Esta ação será efetivada ao salvar o formulário.'
      );
      if (!confirmDelete) return;
    }
    
    setEnderecos(enderecos.filter((_, i) => i !== index));
    toast.success('Endereço removido. Clique em Salvar para confirmar.');
  };

  const updateEndereco = (index, field, value) => {
    const updated = [...enderecos];
    updated[index][field] = value;
    setEnderecos(updated);
  };

  const addTelefone = () => {
    setTelefones([...telefones, {
      numero: '',
      tipo: 'celular',
      whatsapp: false,
      principal: telefones.length === 0
    }]);
  };

  const removeTelefone = async (index) => {
    const telefone = telefones[index];
    
    if (telefone.id && isEdit) {
      const confirmDelete = window.confirm(
        'Tem certeza que deseja remover este telefone? Esta ação será efetivada ao salvar o formulário.'
      );
      if (!confirmDelete) return;
    }
    
    setTelefones(telefones.filter((_, i) => i !== index));
    toast.success('Telefone removido. Clique em Salvar para confirmar.');
  };

  const updateTelefone = (index, field, value) => {
    const updated = [...telefones];
    updated[index][field] = value;
    
    if (field === 'principal' && value) {
      updated.forEach((tel, i) => {
        if (i !== index) tel.principal = false;
      });
    }
    
    setTelefones(updated);
  };

  const buscarCep = async (cep, index) => {
    if (cep.length === 9) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          updateEndereco(index, 'logradouro', data.logradouro);
          updateEndereco(index, 'bairro', data.bairro);
          
          const cidade = cidades.find(c => 
            c.nome.toLowerCase() === data.localidade.toLowerCase()
          );
          if (cidade) {
            updateEndereco(index, 'cidadeId', cidade.id);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderDadosBasicosStep();
      case 2:
        return renderEnderecosStep();
      case 3:
        return renderTelefonesStep();
      case 4:
        return renderResumoStep();
      default:
        return null;
    }
  };

  const renderDadosBasicosStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-blue-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          {tipoPessoa === 'F' ? <User className="w-5 h-5 text-blue-500" /> : <Building className="w-5 h-5 text-blue-500" />}
          Informações Principais
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Pessoa *
            </label>
            <select
              {...register('tipoPessoa')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              disabled={isEdit}
            >
              <option value="F">Pessoa Física</option>
              <option value="J">Pessoa Jurídica</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tipoPessoa === 'F' ? 'CPF' : 'CNPJ'} *
            </label>
            <InputMask
              mask={tipoPessoa === 'F' ? '999.999.999-99' : '99.999.999/9999-99'}
              value={watch('cpfCnpj') || ''}
              onChange={(e) => setValue('cpfCnpj', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              disabled={isEdit}
            />
            {errors.cpfCnpj && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.cpfCnpj.message}
              </p>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tipoPessoa === 'F' ? 'Nome Completo' : 'Razão Social'} *
            </label>
            <input
              type="text"
              {...register('nome')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
            />
            {errors.nome && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.nome.message}
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tipoPessoa === 'F' ? 'RG' : 'Inscrição Estadual'}
            </label>
            <input
              type="text"
              {...register('rgIe')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                {...register('email')}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.email.message}
              </p>
            )}
          </div>
          
          {tipoPessoa === 'F' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data de Nascimento
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    {...register('dataNascimento')}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Pai
                </label>
                <input
                  type="text"
                  {...register('nomePai')}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome da Mãe
                </label>
                <input
                  type="text"
                  {...register('nomeMae')}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dia de Vencimento *
            </label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                min="1"
                max="31"
                {...register('diaVencimento')}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                placeholder="Ex: 10"
              />
            </div>
            {errors.diaVencimento && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.diaVencimento.message}
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Senha do Portal
            </label>
            <input
              type="password"
              {...register('senhaPortal')}
              placeholder={isEdit ? "Deixe vazio para manter a senha atual" : "Senha de acesso"}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações
            </label>
            <textarea
              {...register('observacoes')}
              rows="4"
              placeholder="Informações adicionais sobre o cliente..."
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEnderecosStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-green-500">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
            <MapPin className="w-5 h-5 text-green-500" />
            Endereços do Cliente
          </h2>
          <button
            type="button"
            onClick={addEndereco}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Adicionar Endereço
          </button>
        </div>
        
        <div className="space-y-4">
          {enderecos.map((endereco, index) => (
            <div key={index} className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-green-300 dark:hover:border-green-700 transition-colors">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Endereço {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeEndereco(index)}
                  className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remover endereço"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    CEP
                  </label>
                  <InputMask
                    mask="99999-999"
                    value={endereco.cep}
                    onChange={(e) => {
                      updateEndereco(index, 'cep', e.target.value);
                      buscarCep(e.target.value, index);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Logradouro
                  </label>
                  <input
                    type="text"
                    value={endereco.logradouro}
                    onChange={(e) => updateEndereco(index, 'logradouro', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    value={endereco.numero}
                    onChange={(e) => updateEndereco(index, 'numero', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={endereco.complemento}
                    onChange={(e) => updateEndereco(index, 'complemento', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Bairro
                  </label>
                  <input
                    type="text"
                    value={endereco.bairro}
                    onChange={(e) => updateEndereco(index, 'bairro', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Cidade
                  </label>
                  <select
                    value={endereco.cidadeId}
                    onChange={(e) => updateEndereco(index, 'cidadeId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  >
                    <option value="">Selecione...</option>
                    {cidades.map(cidade => (
                      <option key={cidade.id} value={cidade.id}>
                        {cidade.nome}/{cidade.estado}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Tipo
                  </label>
                  <select
                    value={endereco.tipo}
                    onChange={(e) => updateEndereco(index, 'tipo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  >
                    <option value="instalacao">Instalação</option>
                    <option value="cobranca">Cobrança</option>
                    <option value="correspondencia">Correspondência</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Referência
                  </label>
                  <input
                    type="text"
                    value={endereco.referencia || ''}
                    onChange={(e) => updateEndereco(index, 'referencia', e.target.value)}
                    placeholder="Ex: Próximo ao metrô, em frente à padaria..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {enderecos.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <MapPin className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p>Nenhum endereço cadastrado</p>
              <p className="text-sm mt-1">Clique em "Adicionar Endereço" para começar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTelefonesStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-purple-500">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
            <Phone className="w-5 h-5 text-purple-500" />
            Telefones de Contato
          </h2>
          <button
            type="button"
            onClick={addTelefone}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Adicionar Telefone
          </button>
        </div>
        
        <div className="space-y-3">
          {telefones.map((telefone, index) => (
            <div key={index} className="flex items-center gap-3 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-300 dark:hover:border-purple-700 transition-colors">
              <div className="flex-1">
                <InputMask
                  mask={telefone.tipo === 'fixo' ? '(99) 9999-9999' : '(99) 99999-9999'}
                  value={telefone.numero}
                  onChange={(e) => updateTelefone(index, 'numero', e.target.value)}
                  placeholder="(00) 00000-0000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-800"
                />
              </div>
              
              <select
                value={telefone.tipo}
                onChange={(e) => updateTelefone(index, 'tipo', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-800"
              >
                <option value="celular">Celular</option>
                <option value="fixo">Fixo</option>
                <option value="comercial">Comercial</option>
              </select>
              
              <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={telefone.whatsapp}
                  onChange={(e) => updateTelefone(index, 'whatsapp', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                WhatsApp
              </label>
              
              <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
                <input
                  type="radio"
                  name="principal"
                  checked={telefone.principal}
                  onChange={() => updateTelefone(index, 'principal', true)}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                Principal
              </label>
              
              <button
                type="button"
                onClick={() => removeTelefone(index)}
                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Remover telefone"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
          
          {telefones.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Phone className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p>Nenhum telefone cadastrado</p>
              <p className="text-sm mt-1">Clique em "Adicionar Telefone" para começar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderResumoStep = () => {
    const dadosFormulario = watch();
    
    return (
      <div className="space-y-6">
        {/* Dados Básicos */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-blue-500">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            {dadosFormulario.tipoPessoa === 'F' ? <User className="w-5 h-5 text-blue-500" /> : <Building className="w-5 h-5 text-blue-500" />}
            Dados do Cliente
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tipo</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {dadosFormulario.tipoPessoa === 'F' ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {dadosFormulario.tipoPessoa === 'F' ? 'CPF' : 'CNPJ'}
              </p>
              <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.cpfCnpj}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">Nome</p>
              <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.nome}</p>
            </div>
            {dadosFormulario.rgIe && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {dadosFormulario.tipoPessoa === 'F' ? 'RG' : 'Inscrição Estadual'}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.rgIe}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
              <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.email}</p>
            </div>
            {dadosFormulario.tipoPessoa === 'F' && dadosFormulario.dataNascimento && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Data de Nascimento</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(dadosFormulario.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                </p>
              </div>
            )}
            {dadosFormulario.tipoPessoa === 'F' && dadosFormulario.nomePai && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Nome do Pai</p>
                <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.nomePai}</p>
              </div>
            )}
            {dadosFormulario.tipoPessoa === 'F' && dadosFormulario.nomeMae && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Nome da Mãe</p>
                <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.nomeMae}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Dia de Vencimento</p>
              <p className="font-medium text-gray-900 dark:text-white">Dia {dadosFormulario.diaVencimento}</p>
            </div>
            {dadosFormulario.observacoes && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">Observações</p>
                <p className="font-medium text-gray-900 dark:text-white">{dadosFormulario.observacoes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Endereços */}
        {enderecos.length > 0 && (
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-green-500">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <MapPin className="w-5 h-5 text-green-500" />
              Endereços ({enderecos.length})
            </h3>
            <div className="space-y-3">
              {enderecos.map((endereco, index) => {
                const cidade = cidades.find(c => c.id == endereco.cidadeId);
                return (
                  <div key={index} className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          Endereço {index + 1} - {endereco.tipo.charAt(0).toUpperCase() + endereco.tipo.slice(1)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {endereco.logradouro}, {endereco.numero}
                          {endereco.complemento && `, ${endereco.complemento}`}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {endereco.bairro} - CEP: {endereco.cep}
                        </p>
                        {cidade && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {cidade.nome}/{cidade.estado}
                          </p>
                        )}
                        {endereco.referencia && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Ref: {endereco.referencia}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Telefones */}
        {telefones.length > 0 && (
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-purple-500">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Phone className="w-5 h-5 text-purple-500" />
              Telefones ({telefones.length})
            </h3>
            <div className="space-y-2">
              {telefones.map((telefone, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="font-medium text-gray-900 dark:text-white">{telefone.numero}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded capitalize">
                      {telefone.tipo}
                    </span>
                    {telefone.whatsapp && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded">
                        WhatsApp
                      </span>
                    )}
                    {telefone.principal && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                        Principal
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerta Final */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-400">Revise as informações</p>
              <p className="text-sm text-yellow-700 dark:text-yellow-500 mt-1">
                Certifique-se de que todos os dados estão corretos antes de finalizar o cadastro.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clientes')}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isEdit ? 'Atualize as informações do cliente' : 'Preencha os dados para cadastrar um novo cliente'}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all
                  ${currentStep > step.id 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : currentStep === step.id 
                    ? 'bg-blue-500 border-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                  }
                `}>
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <div className="text-center mt-2">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.id 
                      ? 'text-gray-900 dark:text-white' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
                    {step.description}
                  </p>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`
                  flex-1 h-0.5 mx-2 transition-all
                  ${currentStep > step.id 
                    ? 'bg-green-500' 
                    : 'bg-gray-300 dark:bg-gray-700'
                  }
                `} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} id="cliente-form">
        {renderStepContent()}
      </form>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center sticky bottom-0 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={previousStep}
          disabled={currentStep === 1}
          className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </button>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/clientes')}
            className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all flex items-center gap-2 font-medium"
          >
            <X className="w-4 h-4" />
            Cancelar
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                nextStep(e);
              }}
              className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Próximo
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="submit"
              form="cliente-form"
              disabled={loading}
              className="px-8 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : isEdit ? 'Atualizar Cliente' : 'Finalizar Cadastro'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClienteForm;